import {
    Component,
    ChangeDetectorRef,
    computed,
    input,
    output,
    viewChild,
    ChangeDetectionStrategy,
} from "@angular/core";
import { FormBuilder, FormGroup, FormsModule, ReactiveFormsModule, Validators } from "@angular/forms";
import { TypeManagerDecorator } from "../../../../main/type.map.service";
import { S25Util } from "../../../../util/s25-util";
import { Budget } from "../Budget";
import { BudgetLabelPhrases, BudgetLabels } from "../budget.labels";
import { BudgetPlanService } from "./budget.plan.service";
import { S25ButtonComponent } from "../../../../standalone/s25.button.component";
import { S25ModalModule } from "../../../s25-modal/s25.modal.module";
import { S25DropdownPaginatedModule } from "../../../s25-dropdown/s25.dropdown.paginated.module";
import { S25ModalComponent } from "../../../s25-modal/s25.modal.component";

@TypeManagerDecorator("s25-ng-budget-plan-modal")
@Component({
    selector: "s25-ng-budget-plan-modal",
    template: `
        <s25-ng-modal #budgetPlanModal [title]="modalTitle" [type]="'custom'" [size]="'lg'">
            <ng-template #s25ModalBody>
                @if (showBudgetPlanForm) {
                    <form [formGroup]="budgetPlanForm" (ngSubmit)="onSubmit()" class="budget-form">
                        <!-- Basic Information -->
                        <div class="form-section">
                            <h4>Basic Information</h4>

                            <div class="form-row">
                                <div class="form-field">
                                    <label for="planName" class="required">Plan Name</label>
                                    <input
                                        type="text"
                                        id="planName"
                                        class="c-input"
                                        formControlName="name"
                                        placeholder="Enter plan name"
                                        maxlength="100"
                                    />

                                    @if (budgetPlanForm.get("name")?.invalid && budgetPlanForm.get("name")?.touched) {
                                        <div class="validation-error">
                                            @if (budgetPlanForm.get("name")?.errors?.["required"]) {
                                                Plan name is required
                                            }
                                            @if (budgetPlanForm.get("name")?.errors?.["minlength"]) {
                                                Plan name must be at least 3 characters
                                            }
                                        </div>
                                    }
                                </div>

                                <div class="form-field">
                                    <label for="planType" class="required">Plan Type</label>
                                    <s25-ng-dropdown-search-criteria
                                        type="budgetPlanTypes"
                                        formControlName="planType"
                                        placeholder="Select plan type"
                                    />
                                    @if (
                                        budgetPlanForm.get("planType")?.invalid &&
                                        budgetPlanForm.get("planType")?.touched
                                    ) {
                                        <div class="validation-error">Plan type is required</div>
                                    }
                                </div>
                            </div>

                            <div class="form-row">
                                <div class="form-field">
                                    <label for="budget" class="required">{{ labels.ALLOCATION }}</label>
                                    <s25-ng-dropdown-search-criteria
                                        type="budgets"
                                        formControlName="budget"
                                        [customFilterValue]="organizationIdAsFilterValue()"
                                        [placeholder]="labelPhrases.selectAllocation"
                                        (valueChange)="onBudgetSelect($event)"
                                    />
                                </div>
                            </div>

                            <div class="form-row">
                                <div class="form-field full-width">
                                    <label for="planDescription">Description</label>
                                    <textarea
                                        id="planDescription"
                                        class="c-input"
                                        formControlName="description"
                                        placeholder="Enter plan description (optional)"
                                        rows="3"
                                        maxlength="500"
                                    ></textarea>
                                </div>
                            </div>
                        </div>

                        <!-- Budget Allocation -->
                        <div class="form-section">
                            <h4>{{ labels.ALLOCATION_ALLOCATION }}</h4>

                            <div class="form-row">
                                <div class="form-field">
                                    <label for="allocatedAmount" class="required">Allocated Amount</label>
                                    <input
                                        type="number"
                                        id="allocatedAmount"
                                        class="c-input"
                                        formControlName="allocatedAmount"
                                        placeholder="0.00"
                                        min="0"
                                        step="0.01"
                                    />
                                    @if (
                                        budgetPlanForm.get("allocatedAmount")?.invalid &&
                                        budgetPlanForm.get("allocatedAmount")?.touched
                                    ) {
                                        <div class="validation-error">
                                            @if (budgetPlanForm.get("allocatedAmount")?.errors?.["required"]) {
                                                Allocated amount is required
                                            }
                                            @if (budgetPlanForm.get("allocatedAmount")?.errors?.["min"]) {
                                                Amount must be greater than 0
                                            }
                                        </div>
                                    }
                                </div>
                            </div>

                            <div class="form-row">
                                <div class="form-field">
                                    <label for="startDate" class="required">Start Date</label>
                                    <input
                                        type="date"
                                        id="startDate"
                                        class="c-input"
                                        formControlName="startDate"
                                        (change)="onStartDateChange($event.target.value)"
                                    />
                                    @if (
                                        budgetPlanForm.get("startDate")?.invalid &&
                                        budgetPlanForm.get("startDate")?.touched
                                    ) {
                                        <div class="validation-error">Start date is required</div>
                                    }
                                </div>

                                <div class="form-field">
                                    <label for="endDate" class="required">End Date</label>
                                    <input
                                        type="date"
                                        id="endDate"
                                        class="c-input"
                                        formControlName="endDate"
                                        [min]="minEndDate ? minEndDate.toISOString().split('T')[0] : ''"
                                    />
                                    @if (
                                        budgetPlanForm.get("endDate")?.invalid && budgetPlanForm.get("endDate")?.touched
                                    ) {
                                        <div class="validation-error">End date is required</div>
                                    }
                                </div>
                            </div>

                            @if (budgetPlanForm.errors?.["dateRange"]) {
                                <div class="validation-error">
                                    End date must be the same day or after the start date
                                </div>
                            }
                        </div>

                        <!-- Additional Information -->
                        <div class="form-section">
                            <h4>Additional Information</h4>

                            <div class="form-row">
                                <div class="form-field">
                                    <label for="comments">Comments</label>
                                    <textarea
                                        id="comments"
                                        class="c-input"
                                        formControlName="comments"
                                        placeholder="Additional comments or notes (optional)"
                                        rows="3"
                                        maxlength="1000"
                                    ></textarea>
                                </div>
                            </div>

                            <div class="info-box">
                                @if (planRequiresApproval()) {
                                    <strong>Note:</strong> {{ labelPhrases.budgetsSavedAsDraftsNote }}
                                    use the "Submit for Approval" button to submit for workflow approval.
                                } @else {
                                    <strong>Note:</strong> {{ labelPhrases.allocationNoWorkflowNote }}
                                    auto-approved when saved.
                                }
                            </div>
                        </div>

                        <!-- Form Actions -->
                        <div class="form-actions">
                            <s25-ng-button
                                [type]="'outline'"
                                [buttonText]="'Cancel'"
                                [onClick]="cancelForm"
                            ></s25-ng-button>
                            <s25-ng-button
                                [type]="'primary'"
                                [buttonText]="getSubmitButtonText()"
                                [onClick]="onSubmit"
                                [isLoading]="isSubmitting"
                                [loadingText]="'Saving...'"
                                [disabled]="budgetPlanForm.invalid"
                            ></s25-ng-button>
                        </div>
                    </form>
                }
            </ng-template>
        </s25-ng-modal>
    `,
    styles: `
        .budget-form {
            display: flex;
            flex-direction: column;
            gap: 1.5rem;
        }

        .form-section {
            border: 1px solid var(--color--border);
            border-radius: 8px;
            padding: 1.5rem;
            background: var(--color--page-bg);
        }

        .form-section h4 {
            margin: 0 0 1rem 0;
            color: var(--color--text);
            font-size: 1.1rem;
            font-weight: 600;
        }

        .form-row {
            display: grid;
            grid-template-columns: 1fr 1fr;
            gap: 1.5rem;
            margin-bottom: 1rem;
        }

        .form-row:last-child {
            margin-bottom: 0;
        }

        .form-field {
            display: flex;
            flex-direction: column;
        }

        .form-field.full-width {
            grid-column: 1 / -1;
        }

        .form-field label {
            font-weight: 600;
            color: var(--color--text);
            margin-bottom: 0.5rem;
            font-size: 0.9rem;
        }

        .form-field label.required::after {
            content: " *";
            color: var(--color--danger);
        }

        .form-field select {
            border: 1px solid var(--color--form-border);
            border-radius: 4px;
            font-size: 0.9rem;
            background: var(--color--form-bg);
            color: var(--color--text);
        }

        .form-field select:focus {
            outline: none;
            border-color: var(--cn-blue);
            box-shadow: var(--shadow--form-focus);
        }

        .validation-error {
            color: var(--color--danger);
            font-size: 0.8rem;
            margin-top: 0.25rem;
        }

        .help-text {
            font-size: 0.85rem;
            color: var(--color--text-muted);
            margin-top: 0.5rem;
            line-height: 1.5;
        }

        #planDescription {
            height: unset;
        }

        .info-box {
            background: color-mix(in srgb, var(--cn-blue) 15%, var(--color--surface-bg));
            border-left: 4px solid var(--cn-blue);
            padding: 1rem;
            border-radius: 4px;
            font-size: 0.9rem;
            color: var(--color--link-text-hover);
            margin-top: 1rem;
        }

        .form-actions {
            display: flex;
            gap: 1rem;
            justify-content: flex-end;
            padding-top: 1rem;
            border-top: 1px solid var(--color--border);
        }

        @media (max-width: 768px) {
            .form-row {
                grid-template-columns: 1fr;
                gap: 1rem;
            }

            .form-actions s25-ng-button {
                width: 100%;
            }
        }
    `,
    standalone: true,
    changeDetection: ChangeDetectionStrategy.Eager,
    imports: [S25ButtonComponent, S25ModalModule, FormsModule, ReactiveFormsModule, S25DropdownPaginatedModule],
})
export class BudgetPlanModalComponent {
    readonly labels = BudgetLabels;
    readonly labelPhrases = BudgetLabelPhrases;

    readonly orgId = input.required<number>();
    /** Reserved for future edit-by-plan flows; parent may bind without the modal reading it yet. */
    readonly planId = input<number | undefined>(undefined);
    readonly preselectedBudget = input<Budget.OrgBudget | null | undefined>(undefined);

    readonly planSaved = output<Budget.BudgetPlan>();
    readonly planCancelled = output<void>();

    readonly budgetPlanModal = viewChild.required<S25ModalComponent>("budgetPlanModal");

    /** `s25-ng-dropdown-search-criteria` expects `customFilterValue` as a string (query fragment). */
    readonly organizationIdAsFilterValue = computed(() => String(this.orgId()));

    budgetPlanForm: FormGroup;
    minEndDate: Date | null = null;

    showBudgetPlanForm = false;
    isEditMode = false;
    editingBudgetPlan: Budget.BudgetPlan | null = null;
    isSubmitting = false;

    get modalTitle(): string {
        return this.isEditMode ? BudgetLabelPhrases.editBudget : BudgetLabelPhrases.createBudget;
    }

    constructor(
        private fb: FormBuilder,
        private cd: ChangeDetectorRef,
    ) {
        this.budgetPlanForm = this.createForm();
    }

    private createForm(): FormGroup {
        return this.fb.group(
            {
                name: ["", [Validators.required, Validators.minLength(3)]],
                budget: [null, Validators.required],
                description: [""],
                planType: ["", Validators.required],
                allocatedAmount: [0, [Validators.required, Validators.min(0)]],
                startDate: [null, Validators.required],
                endDate: [null, Validators.required],
                comments: [""],
            },
            { validators: this.dateRangeValidator },
        );
    }

    private dateRangeValidator(group: FormGroup) {
        const startDate = group.get("startDate")?.value;
        const endDate = group.get("endDate")?.value;

        if (startDate && endDate && startDate > endDate) {
            return { dateRange: true };
        }

        return null;
    }

    openCreateModal(): void {
        this.isEditMode = false;
        this.editingBudgetPlan = null;
        this.resetForm();
        this.applyPreselectedBudget();
        this.showBudgetPlanForm = true;
        this.budgetPlanModal().open();
    }

    private applyPreselectedBudget(): void {
        const budget = this.preselectedBudget();
        if (!budget) return;

        this.budgetPlanForm.get("budget")?.setValue({ itemId: budget.budgetId, itemName: budget.name });

        const startDateStr = budget.startDate ? S25Util.date.toS25ISODateStr(budget.startDate) : null;
        const endDateStr = budget.endDate ? S25Util.date.toS25ISODateStr(budget.endDate) : null;

        if (startDateStr) {
            this.budgetPlanForm.get("startDate")?.setValue(startDateStr);
            this.minEndDate = new Date(startDateStr);
        }
        if (endDateStr) {
            this.budgetPlanForm.get("endDate")?.setValue(endDateStr);
        }
    }

    openEditModal(plan: Budget.BudgetPlan): void {
        this.isEditMode = true;
        this.editingBudgetPlan = plan;
        this.populateForm(plan);
        this.showBudgetPlanForm = true;
        this.budgetPlanModal().open();
    }

    private populateForm(plan: Budget.BudgetPlan): void {
        // Handle parsed value format for amounts
        const allocatedAmount =
            typeof plan.allocatedAmount === "number" ? plan.allocatedAmount : (plan.allocatedAmount?.parsedValue ?? 0);

        this.budgetPlanForm.patchValue({
            name: plan.name,
            budget: { itemId: plan.budgetId },
            description: plan.description || "",
            planType: Budget.budgetPlanTypeToDropdownItem(plan.planType),
            allocatedAmount: allocatedAmount,
            startDate: S25Util.date.toS25ISODateStr(plan.startDate),
            endDate: S25Util.date.toS25ISODateStr(plan.endDate),
            comments: plan.comments || "",
        });

        // Set minimum end date based on start date
        if (plan.startDate) {
            this.minEndDate = new Date(plan.startDate);
        }
    }

    getSubmitButtonText(): string {
        return this.isEditMode ? "Save Draft" : "Create Draft";
    }

    planRequiresApproval(): boolean {
        if (this.isEditMode && this.editingBudgetPlan) {
            return !!this.editingBudgetPlan.workflowProcessId;
        }
        return !!this.preselectedBudget()?.workflowProcessId;
    }

    onStartDateChange(dateString: string): void {
        if (dateString) {
            this.minEndDate = new Date(dateString);
            this.cd.detectChanges();
        }
    }

    onSubmit = async (): Promise<void> => {
        if (this.budgetPlanForm.invalid) {
            this.markFormGroupTouched();
            return;
        }

        this.isSubmitting = true;
        this.cd.detectChanges();

        try {
            const formValue = this.budgetPlanForm.value;
            const planData: any = {
                name: formValue.name,
                description: formValue.description,
                planType: Budget.budgetPlanTypeCodeFromDropdownItem(formValue.planType),
                allocatedAmount: formValue.allocatedAmount,
                startDate: S25Util.date.toS25ISODateTimeStr(formValue.startDate),
                endDate: S25Util.date.toS25ISODateTimeStr(formValue.endDate),
                // comments: formValue.comments, //no comments currently
                budgetId: formValue.budget.itemId,
                // Always create/update as draft - submission happens via separate button
                workflowStatus: null,
            };

            let result: Budget.BudgetPlanResponse;

            if (this.isEditMode && this.editingBudgetPlan) {
                const [updateResult, updateError] = await S25Util.Maybe(
                    BudgetPlanService.updateBudgetPlan(this.editingBudgetPlan.budgetPlanId, planData),
                );
                if (updateError) {
                    throw new Error(BudgetLabelPhrases.failedUpdateBudget(S25Util.errorText(updateError) || ""));
                }
                result = updateResult;
            } else {
                const [createResult, createError] = await S25Util.Maybe(BudgetPlanService.createBudgetPlan(planData));
                if (createError) {
                    throw new Error(BudgetLabelPhrases.failedCreateBudget(S25Util.errorText(createError) || ""));
                }
                result = createResult;
            }

            this.planSaved.emit(result.budgetPlan);

            // Close modal
            this.showBudgetPlanForm = false;
            this.budgetPlanModal().close();
        } catch (error) {
            S25Util.showError(error, BudgetLabelPhrases.failedSaveBudget(this.isEditMode));
        } finally {
            this.isSubmitting = false;
            this.cd.detectChanges();
        }
    };

    cancelForm = () => {
        this.showBudgetPlanForm = false;
        this.budgetPlanModal().close();
        this.resetForm();
        this.planCancelled.emit();
    };

    onBudgetSelect(item: Budget.BudgetDropdownItem): void {
        if (item && item.startDate && item.endDate) {
            const startDateStr = S25Util.date.toS25ISODateStr(item.startDate);
            const endDateStr = S25Util.date.toS25ISODateStr(item.endDate);

            // Set the start/end date from the parent budget unless they're already set by the user
            if (startDateStr && !this.budgetPlanForm.get("startDate")?.value) {
                this.budgetPlanForm.get("startDate")?.setValue(startDateStr);
                this.minEndDate = new Date(startDateStr);
            }
            if (endDateStr && !this.budgetPlanForm.get("endDate")?.value) {
                this.budgetPlanForm.get("endDate")?.setValue(endDateStr);
            }

            // Trigger change detection
            this.cd.detectChanges();
        }
    }

    private resetForm(): void {
        this.budgetPlanForm.reset();
        this.isEditMode = false;
        this.editingBudgetPlan = null;
        this.minEndDate = null;
    }

    private markFormGroupTouched(): void {
        Object.keys(this.budgetPlanForm.controls).forEach((fieldKey) => {
            const control = this.budgetPlanForm.get(fieldKey);
            control?.markAsTouched();
        });
    }
}
